// KpiComplianceReport — the Reports tab's KPI compliance view: how much of
// what the contract promised was delivered, month by month, and what has been
// recorded against the months it was not.
//
// The view holds no derivation. Every figure arrives from the server, which
// normalises heterogeneous KPI cadences onto the calendar-month axis the
// other reports use, so a daily, a weekly and a quarterly KPI share one chart
// and one headline. The KPI tab computes the same definition from the same
// shared fixture, so the two surfaces state the same numbers.
//
// A month nobody reported in is a gap everywhere: no column on the chart, a
// dash in the table, an outlined cell on the heatmap. It is never a nought,
// which would say every KPI failed when the truth is that nothing was
// measured.
//
// Credits are READ-ONLY here. They are recorded on the KPIs tab, beside the
// results that justify them, and the report sums only the amounts somebody
// entered — the KPI model's credit formula is display-only text that is never
// evaluated, so an accrued figure cannot honestly be derived.
//
// Failure is whole-view by design, like the delivery report: one data domain
// backs it, so a failed read leaves nothing to render honestly.

import { Alert, LoadingSpinner } from "../../common";
import MetricCard from "../tracking/deliverable/MetricCard";
import ReportSection from "./ReportSection";
import KpiHeatmap from "./KpiHeatmap";
import { TD_CLASS, TD_NUMERIC_CLASS, Th } from "./reportTable";
import ComplianceColumns from "./charts/ComplianceColumns";
import {
  countTone,
  formatCompliancePercent,
  formatReportMonth,
} from "./reportFormat";
import { formatAud2dp } from "../../../utils/formatCurrency";
import { formatDayMonthYear } from "../../../utils/formatShortDate";
import { useReportKpi } from "../../../hooks/useContractReport";
import type {
  KpiComplianceValue,
  KpiDetailRow,
  KpiReportModel,
  ReportRange,
  ServiceCredit,
} from "../../../types/report";

export interface KpiComplianceReportProps {
  contractId: string;
  /** Owned by the Reports tab, which renders the range chips for every
   *  focused view. */
  range: ReportRange;
}

export default function KpiComplianceReport({
  contractId,
  range,
}: KpiComplianceReportProps) {
  const { data, isPending, error } = useReportKpi(contractId, range);

  if (isPending) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Alert variant="error">
        The KPI compliance report couldn&apos;t be loaded. Refresh to try
        again.
      </Alert>
    );
  }

  return <KpiBody model={data} />;
}

function KpiBody({ model }: { model: KpiReportModel }) {
  const { metrics, complianceSeries, heatmap, kpiDetail, credits } = model;
  const months = complianceSeries.map((point) => point.month);
  const uncostedBreaches = metrics.breachedMonths - metrics.costedBreachedMonths;

  if (heatmap.length === 0) {
    return (
      <ReportSection id="kpi-overview" title="KPI compliance">
        <p
          data-testid="kpi-report-empty"
          className="text-body-sm text-slate-500 dark:text-slate-400"
        >
          This contract has no KPIs defined. Add them on the KPIs tab to start
          reporting compliance.
        </p>
      </ReportSection>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <MetricCard
            testId="kpi-last-month"
            label="Last closed month"
            value={formatCompliancePercent(metrics.lastMonthWeightedPct)}
            subtitle={
              metrics.lastClosedMonth === null
                ? "No month has closed with a result"
                : `${formatReportMonth(metrics.lastClosedMonth)} · ${metrics.lastMonthMet} of ${metrics.lastMonthMeasured} met`
            }
          />
          <MetricCard
            testId="kpi-range-average"
            label="Range average"
            value={formatCompliancePercent(metrics.rangeWeightedPct)}
            subtitle="Equal weight per reporting KPI"
          />
          <MetricCard
            testId="kpi-breaches"
            label="Breached results"
            value={metrics.breachCount}
            valueTone={countTone(metrics.breachCount, "danger")}
            subtitle={`across ${metrics.breachedMonths} months`}
          />
          <MetricCard
            testId="kpi-at-risk"
            label="At-risk results"
            value={metrics.atRiskCount}
            valueTone={countTone(metrics.atRiskCount, "warning")}
            subtitle="Inside the tolerance band"
          />
          <MetricCard
            testId="kpi-credits-recorded"
            // "Recorded", never "accrued": this is the sum of amounts someone
            // entered, not an evaluation of any credit formula.
            label="Service credits recorded"
            value={formatAud2dp(metrics.creditsTotal)}
            subtitle={`${metrics.costedBreachedMonths} of ${metrics.breachedMonths} breached months costed · ex-GST`}
          />
        </div>
        {uncostedBreaches > 0 && (
          <p
            data-testid="kpi-uncosted-breaches"
            className="mt-2 text-caption text-amber-700 dark:text-amber-400"
          >
            {uncostedBreaches} breached {uncostedBreaches === 1 ? "month has" : "months have"}{" "}
            no service credit recorded. Record one on the KPIs tab, or the
            breach reads as no financial impact.
          </p>
        )}
      </div>

      <ReportSection id="kpi-chart" title="Compliance by month">
        <ComplianceColumns
          series={complianceSeries}
          rangeWeightedPct={metrics.rangeWeightedPct}
        />
      </ReportSection>

      <ReportSection id="kpi-months" title="Monthly compliance">
        <MonthlyComplianceTable series={complianceSeries} />
      </ReportSection>

      <ReportSection id="kpi-heatmap-section" title="KPI performance by month">
        <KpiHeatmap rows={heatmap} months={months} />
        <HeatmapKey />
      </ReportSection>

      <ReportSection id="kpi-detail" title="KPI detail">
        <KpiDetailTable rows={kpiDetail} />
      </ReportSection>

      <ReportSection id="kpi-credits" title="Service credits recorded">
        {credits.length === 0 ? (
          <p
            data-testid="kpi-credits-empty"
            className="text-body-sm text-slate-500 dark:text-slate-400"
          >
            No service credit has been recorded in this window. Credits are
            recorded on the KPIs tab, beside the results that justify them.
          </p>
        ) : (
          <CreditsTable credits={credits} />
        )}
      </ReportSection>
    </div>
  );
}

/** The numeric read-out the chart points at. Every month in the window has a
 *  row, so a gap is visible as a dash rather than as a missing line. */
function MonthlyComplianceTable({ series }: { series: KpiComplianceValue[] }) {
  return (
    <div
      role="group"
      aria-label="Monthly compliance"
      tabIndex={0}
      data-testid="kpi-months-table"
      className="overflow-x-auto focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400"
    >
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-200 dark:border-slate-700">
            <Th>Month</Th>
            <Th numeric>Compliance</Th>
          </tr>
        </thead>
        <tbody>
          {series.map((point) => (
            <tr
              key={point.month}
              className="border-b border-slate-100 last:border-0 dark:border-slate-800"
            >
              <td className={TD_CLASS}>{formatReportMonth(point.month)}</td>
              <td className={TD_NUMERIC_CLASS}>
                {formatCompliancePercent(point.weightedPct)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KpiDetailTable({ rows }: { rows: KpiDetailRow[] }) {
  return (
    <div
      role="group"
      aria-label="KPI detail"
      tabIndex={0}
      data-testid="kpi-detail-table"
      className="overflow-x-auto focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400"
    >
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-200 dark:border-slate-700">
            <Th>KPI</Th>
            <Th>Target</Th>
            <Th numeric>Months met</Th>
            <Th numeric>Streak</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.kpiId}
              className="border-b border-slate-100 last:border-0 dark:border-slate-800"
            >
              <th scope="row" className={`${TD_CLASS} text-left font-normal`}>
                {row.kpiName}
              </th>
              <td className={TD_CLASS}>{row.target}</td>
              <td className={TD_NUMERIC_CLASS}>
                {row.metCount} of {row.measuredCount}
              </td>
              <td className={TD_NUMERIC_CLASS}>
                <span aria-hidden="true">{row.streakMonths}</span>
                <span className="sr-only">
                  {row.streakMonths}{" "}
                  {row.streakMonths === 1 ? "month" : "months"} without a
                  breach
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CreditsTable({ credits }: { credits: ServiceCredit[] }) {
  return (
    <div
      role="group"
      aria-label="Service credits recorded"
      tabIndex={0}
      data-testid="kpi-credits-table"
      className="overflow-x-auto focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400"
    >
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-200 dark:border-slate-700">
            <Th>Month</Th>
            <Th>Working</Th>
            <Th numeric>Amount (ex-GST)</Th>
            <Th>Recorded</Th>
          </tr>
        </thead>
        <tbody>
          {credits.map((credit) => (
            <tr
              key={credit.month}
              className="border-b border-slate-100 last:border-0 dark:border-slate-800"
            >
              <td className={TD_CLASS}>{formatReportMonth(credit.month)}</td>
              <td className={TD_CLASS}>{credit.note}</td>
              <td className={TD_NUMERIC_CLASS}>
                {formatAud2dp(credit.amount)}
              </td>
              <td className={TD_CLASS}>
                {credit.updatedByName} ·{" "}
                {formatDayMonthYear(credit.updatedAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Names the five cell tones in text, so the heatmap's colours are legible to
 *  a reader who cannot tell them apart. */
function HeatmapKey() {
  const entries = [
    { label: "Met", className: "bg-emerald-500" },
    { label: "At risk", className: "bg-amber-400" },
    { label: "Breached", className: "bg-red-500" },
    { label: "No measurement due", className: "bg-slate-200 dark:bg-slate-700" },
    {
      label: "Not yet reported",
      className:
        "border border-dashed border-slate-400 dark:border-slate-500",
    },
  ];
  return (
    <p
      data-testid="kpi-heatmap-key"
      className="mt-3 flex flex-wrap items-center gap-4 text-caption text-slate-600 dark:text-slate-400"
    >
      {entries.map((entry) => (
        <span key={entry.label} className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className={`h-2.5 w-2.5 rounded-sm ${entry.className}`}
          />
          {entry.label}
        </span>
      ))}
    </p>
  );
}
